import { useEffect, useMemo, useState } from "react";
import { Calendar } from "../ui/calendar";
import { el, enUS } from "date-fns/locale";
import { api } from "../../lib/api";

/**
 * Visual range-picker that shows booked dates as disabled & striped.
 * Refetches /api/apartments/{aptId}/availability whenever aptId changes.
 */
export default function AvailabilityCalendar({
  aptId,
  range,
  onRangeChange,
  lang = "el",
  t,
}) {
  const [bookedRanges, setBookedRanges] = useState([]);
  const [loading, setLoading] = useState(false);

  // Navigation bounds — visitors can only browse from this month up to +18 months.
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const fromMonth = useMemo(() => new Date(today.getFullYear(), today.getMonth(), 1), [today]);
  const toMonth = useMemo(() => {
    const d = new Date(today.getFullYear(), today.getMonth() + 18, 1);
    return d;
  }, [today]);

  useEffect(() => {
    if (!aptId) return;
    const start = new Date();
    const end = new Date();
    end.setMonth(end.getMonth() + 18);
    const fmt = (d) => d.toISOString().slice(0, 10);
    setLoading(true);
    api
      .get(`/apartments/${aptId}/availability`, {
        params: { start: fmt(start), end: fmt(end) },
      })
      .then((r) => {
        const items = (r.data?.booked || []).map((b) => ({
          from: new Date(b.check_in),
          // check_out is exclusive — last booked night = check_out - 1 day
          to: new Date(new Date(b.check_out).getTime() - 24 * 60 * 60 * 1000),
        }));
        setBookedRanges(items);
      })
      .catch(() => setBookedRanges([]))
      .finally(() => setLoading(false));
  }, [aptId]);

  const disabledMatchers = useMemo(() => {
    const past = { before: today };
    return [past, ...bookedRanges];
  }, [bookedRanges, today]);

  // Responsive month count — single month on mobile, two on lg+
  const numberOfMonths =
    typeof window !== "undefined" && window.innerWidth >= 1024 ? 2 : 1;

  return (
    <div
      data-testid="availability-calendar"
      className="bg-white border border-[#E9E2D3] rounded-2xl p-2 sm:p-4"
    >
      <style>{`
        /* Day cells: fixed square, centered text */
        .availability-cal .rdp-day,
        .availability-cal button[name="day"] {
          width: 36px !important;
          height: 36px !important;
          padding: 0 !important;
          border-radius: 0 !important;
          font-weight: 500;
        }
        /* Booked dates: red diagonal stripe pattern + strikethrough */
        .availability-cal .day-booked,
        .availability-cal .rdp-day_booked {
          background:
            repeating-linear-gradient(
              45deg,
              rgba(220,38,38,0.14),
              rgba(220,38,38,0.14) 4px,
              rgba(220,38,38,0.28) 4px,
              rgba(220,38,38,0.28) 8px
            ) !important;
          color: #b91c1c !important;
          text-decoration: line-through !important;
          opacity: 1 !important;
        }
        /* Past/disabled (non-booked) dates */
        .availability-cal .rdp-day_disabled:not(.day-booked):not(.rdp-day_booked) {
          color: #c5c0b3 !important;
          opacity: 0.55 !important;
        }
        /* Range MIDDLE — sand background, no rounding */
        .availability-cal .rdp-day_range_middle {
          background: #F5F2EB !important;
          color: #1A2B4C !important;
          border-radius: 0 !important;
        }
        /* Range START — rounded LEFT only (connects to middle) */
        .availability-cal .rdp-day_range_start:not(.rdp-day_range_end) {
          background: #1A2B4C !important;
          color: #fff !important;
          border-radius: 6px 0 0 6px !important;
        }
        /* Range END — rounded RIGHT only (connects from middle) */
        .availability-cal .rdp-day_range_end:not(.rdp-day_range_start) {
          background: #1A2B4C !important;
          color: #fff !important;
          border-radius: 0 6px 6px 0 !important;
        }
        /* Same-day selection (start==end) — fully rounded */
        .availability-cal .rdp-day_range_start.rdp-day_range_end,
        .availability-cal .rdp-day_selected:not(.rdp-day_range_start):not(.rdp-day_range_end) {
          background: #1A2B4C !important;
          color: #fff !important;
          border-radius: 6px !important;
        }
        /* Suppress the cell-level highlight added by shadcn to avoid the
           "bleed into previous/next" visual artifact */
        .availability-cal .rdp-cell {
          background: transparent !important;
          border-radius: 0 !important;
          padding: 0 !important;
        }
        /* Today indicator (when not selected) — subtle ring */
        .availability-cal .rdp-day_today:not(.rdp-day_selected):not(.day-booked) {
          background: #FAF7EE !important;
          color: #1A2B4C !important;
          font-weight: 700 !important;
          box-shadow: inset 0 0 0 1.5px #1A2B4C !important;
          border-radius: 6px !important;
        }
        /* Caption — make the month + YEAR very visible */
        .availability-cal .rdp-caption_label {
          font-family: Georgia, serif !important;
          color: #1A2B4C !important;
          font-size: 16px !important;
          font-weight: 600 !important;
          text-transform: capitalize !important;
        }
        .availability-cal .rdp-head_cell {
          color: #6B705C;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 1px;
          width: 36px !important;
        }
      `}</style>
      <div className="availability-cal">
        <Calendar
          mode="range"
          numberOfMonths={numberOfMonths}
          selected={range}
          onSelect={onRangeChange}
          disabled={disabledMatchers}
          modifiers={{ booked: bookedRanges }}
          modifiersClassNames={{ booked: "day-booked" }}
          locale={lang === "el" ? el : enUS}
          weekStartsOn={1}
          showOutsideDays={false}
          defaultMonth={today}
          fromMonth={fromMonth}
          toMonth={toMonth}
          className="!p-0"
        />
      </div>
      <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-3 pt-3 border-t border-[#E9E2D3] text-[11px] text-[#6B705C]">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-[#1A2B4C]" />
          {t?.booking?.legendSelected || "Your selection"}
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-3 rounded-sm"
            style={{
              background:
                "repeating-linear-gradient(45deg, rgba(220,38,38,0.22), rgba(220,38,38,0.22) 2px, rgba(220,38,38,0.4) 2px, rgba(220,38,38,0.4) 4px)",
            }}
          />
          {t?.booking?.legendBooked || "Booked"}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm border border-[#E9E2D3] bg-white" />
          {t?.booking?.legendAvailable || "Available"}
        </span>
        {loading && <span className="ml-auto italic">…</span>}
      </div>
    </div>
  );
}
